import React from 'react'
import {
  defaultColumnHeaders,
  defaultRowHeaders,
  getColumnTypes,
  getTableDimensions,
  parseTableCells,
  resizeHeaders,
  buildTableColumnConditionalEditFieldId,
  buildTableRowConditionalEditFieldId,
  resolvePrefillEditFieldId
} from '../editForm/tableFieldUtils'
import './submissionEditValueDisplay.scss'
import { normalizeFormData } from './submissionTableFlatten'

export interface FormFieldDisplayMeta {
  type: string
  label: string
  rows?: number
  cols?: number
  rowHeaders?: string[]
  columnHeaders?: string[]
  columnTypes?: string[]
  showRowHeaders?: boolean
}

const flattenFormPages = (formData: unknown): any[] => {
  const normalized = normalizeFormData(formData)
  if (!normalized) return []
  let pages = Array.isArray(normalized) ? normalized : []
  if (pages.length > 0 && !Array.isArray(pages[0])) {
    pages = [pages]
  }
  return pages.flat().filter(Boolean)
}

export const buildFormFieldMetaById = (formData: unknown): Map<number, FormFieldDisplayMeta> => {
  const metaById = new Map<number, FormFieldDisplayMeta>()

  flattenFormPages(formData).forEach((element) => {
    const fieldId = Number(element?.fieldId ?? element?.id)
    if (!fieldId) return

    if (element.type === 'table') {
      const { rows, cols } = getTableDimensions(element)
      if (!metaById.has(fieldId)) {
        metaById.set(fieldId, {
          type: 'table',
          label: String(element.label || '').trim(),
          rows,
          cols,
          rowHeaders: resizeHeaders(element.rowHeaders, rows, defaultRowHeaders),
          columnHeaders: resizeHeaders(element.columnHeaders, cols, defaultColumnHeaders),
          columnTypes: getColumnTypes(element),
          showRowHeaders: element.showRowHeaders === true
        })
      }

      const rowSettings: Array<{
        allowEdit?: boolean
        prefillEditFields?: Array<{ fieldId: number; label: string; type?: string }>
      }> = element.tableRowPrefillSettings || []
      const columnSettings: Array<{
        prefillEditFields?: Array<{ fieldId: number; label: string; type?: string }>
      }> = element.tableColumnPrefillSettings || []

      rowSettings.forEach((rowConfig, rowIndex) => {
        if (rowConfig?.allowEdit !== true) return
        ;(rowConfig.prefillEditFields || []).forEach((editField, editFieldIndex) => {
          const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex)
          metaById.set(buildTableRowConditionalEditFieldId(fieldId, rowIndex, editFieldId), {
            type: String(editField.type || 'text'),
            label: String(editField.label || '').trim()
          })
        })
      })

      for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
        for (let colIndex = 0; colIndex < cols; colIndex += 1) {
          const columnEditFields = columnSettings[colIndex]?.prefillEditFields || []
          columnEditFields.forEach((editField, editFieldIndex) => {
            const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex)
            metaById.set(
              buildTableColumnConditionalEditFieldId(fieldId, rowIndex, colIndex, editFieldId),
              {
                type: String(editField.type || 'text'),
                label: String(editField.label || '').trim()
              }
            )
          })
        }
      }
      return
    }

    // First occurrence wins for duplicate top-level fieldIds.
    if (!metaById.has(fieldId)) {
      metaById.set(fieldId, {
        type: String(element.type || 'text'),
        label: String(element.label || '').trim()
      })
    }
    ;(element.prefillEditFields || []).forEach((editField: any, editFieldIndex: number) => {
      const editFieldId = resolvePrefillEditFieldId(editField, editFieldIndex, fieldId)
      if (!editFieldId) return
      metaById.set(editFieldId, {
        type: String(editField.type || 'text'),
        label: String(editField.label || '').trim()
      })
    })
  })

  return metaById
}

const parseTableMatrix = (value: unknown): string[][] | null => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    if (!Array.isArray(value[0])) {
      return [value.map((cell) => String(cell ?? ''))]
    }
    return value.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value)
      if (!Array.isArray(parsed) || parsed.length === 0) return null
      if (!Array.isArray(parsed[0])) {
        return [parsed.map((cell: unknown) => String(cell ?? ''))]
      }
      return parsed.map((row: unknown[]) =>
        Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []
      )
    } catch {
      // A comma-separated string is not a table — addresses and multi-select answers
      // must never be routed into the grid preview.
      return null
    }
  }
  return null
}

const isLikelyTableValue = (value: string, fieldMeta?: FormFieldDisplayMeta): boolean => {
  if (fieldMeta?.type === 'table') {
    const trimmed = value.trim()
    return trimmed.startsWith('[') || trimmed.startsWith('{')
  }
  return Boolean(parseTableMatrix(value))
}

const CHOICE_VALUE_SEPARATOR = /[,;|]/

const parseChoiceSelections = (value: string): string[] => {
  const trimmed = value.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry ?? '').trim()).filter(Boolean)
      }
    } catch {
      // Fall through to separator splitting.
    }
  }
  return trimmed
    .split(CHOICE_VALUE_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean)
}

const isHttpOrRelativeUrl = (value: string): boolean => {
  const trimmed = value.trim()
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith('/') || /^data:/i.test(trimmed)
}

const isLikelyImageUrl = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (/^data:image\//i.test(trimmed)) return true
  if (!isHttpOrRelativeUrl(trimmed)) return false
  // Decode once so "file%20name.png" / paths with spaces still match extension.
  let pathForExt = trimmed
  try {
    pathForExt = decodeURIComponent(trimmed.split('?')[0] || trimmed)
  } catch {
    pathForExt = trimmed.split('?')[0] || trimmed
  }
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)$/i.test(pathForExt)
}

const isLikelyDocumentFileUrl = (value: string): boolean => {
  const trimmed = value.trim()
  if (!/^https?:\/\//i.test(trimmed)) return false
  let pathForExt = trimmed
  try {
    pathForExt = decodeURIComponent(trimmed.split('?')[0] || trimmed)
  } catch {
    pathForExt = trimmed.split('?')[0] || trimmed
  }
  return /\.(pdf|doc|docx|xls|xlsx|zip)$/i.test(pathForExt)
}

const isLikelyFileUrl = (value: string, _fieldMeta?: FormFieldDisplayMeta): boolean => {
  return isLikelyDocumentFileUrl(value)
}

const isImageFieldValue = (
  value: string,
  options?: {
    fieldMeta?: FormFieldDisplayMeta
    editFieldType?: string
    tableColumnType?: string
  }
): boolean => {
  if (!value.trim()) return false
  if (isLikelyImageUrl(value)) return true
  const fieldType = options?.editFieldType || options?.tableColumnType || options?.fieldMeta?.type
  if (fieldType !== 'file') return false
  // File-type fields with an HTTP(S) URL and no document extension → treat as image.
  return /^https?:\/\//i.test(value.trim()) && !isLikelyDocumentFileUrl(value)
}

const ImageViewButton: React.FC<{ url: string; muted?: boolean }> = ({ url, muted = false }) => (
  <a
    href={url}
    target='_blank'
    rel='noreferrer'
    className={`btn btn-sm ${muted ? 'btn-light' : 'btn-light-primary'}`}
  >
    <i className='bi bi-image me-1' aria-hidden='true' />
    View image
  </a>
)

const FileViewButton: React.FC<{ url: string; muted?: boolean }> = ({ url, muted = false }) => (
  <a
    href={url}
    target='_blank'
    rel='noreferrer'
    className={`btn btn-sm ${muted ? 'btn-light' : 'btn-light-primary'}`}
  >
    <i className='bi bi-file-earmark-arrow-up me-1' aria-hidden='true' />
    View file
  </a>
)

const ChoiceValuePreview: React.FC<{ value: string; muted?: boolean }> = ({
  value,
  muted = false
}) => {
  const selections = parseChoiceSelections(value)
  if (selections.length === 0) return <span className='text-muted'>—</span>

  return (
    <div className='d-flex flex-wrap gap-1'>
      {selections.map((selection, index) => (
        <span
          key={`${selection}-${index}`}
          className={`badge border fw-semibold ${
            muted ? 'bg-light text-muted' : 'bg-light text-gray-700'
          }`}
          style={{ wordBreak: 'break-word' }}
        >
          {selection}
        </span>
      ))}
    </div>
  )
}

const inferTableMetaFromValue = (value: string): FormFieldDisplayMeta => {
  const matrix = parseTableMatrix(value)
  const rows = matrix?.length || 1
  const cols = Math.max(...(matrix || [[]]).map((row) => row.length), 1)
  return {
    type: 'table',
    label: 'Table',
    rows,
    cols,
    rowHeaders: defaultRowHeaders(rows),
    columnHeaders: defaultColumnHeaders(cols),
    showRowHeaders: true
  }
}

const parseTableRowFromValue = (value: string, rowIndex: number, tableCols: number): string[] => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return Array.from({ length: tableCols }, () => '')

  try {
    const parsed = JSON.parse(trimmed)
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [trimmed, ...Array.from({ length: Math.max(tableCols - 1, 0) }, () => '')]
    }

    if (Array.isArray(parsed[0])) {
      const row = parsed[rowIndex] ?? parsed[0]
      if (!Array.isArray(row)) return Array.from({ length: tableCols }, () => '')
      return Array.from({ length: tableCols }, (_, colIndex) => String(row[colIndex] ?? ''))
    }

    return Array.from({ length: tableCols }, (_, colIndex) => String(parsed[colIndex] ?? ''))
  } catch {
    return [trimmed, ...Array.from({ length: Math.max(tableCols - 1, 0) }, () => '')]
  }
}

const TableRowValuesTable: React.FC<{
  rowValues: string[]
  tableMeta: FormFieldDisplayMeta
  muted?: boolean
}> = ({ rowValues, tableMeta, muted = false }) => {
  const cols = tableMeta.cols || Math.max(rowValues.length, 1)
  const columnHeaders = tableMeta.columnHeaders || defaultColumnHeaders(cols)
  const columnTypes = getColumnTypes({
    columnTypes: tableMeta.columnTypes,
    tableColumnCount: cols
  })

  return (
    <div className='submission-edit-table-preview'>
      <table className='table table-bordered mb-0 bg-white'>
        <thead>
          <tr>
            {columnHeaders.slice(0, cols).map((header, colIndex) => (
              <th key={`row-edit-col-${colIndex}`} className='text-muted fw-semibold'>
                {header}
                <div className='fw-normal text-muted'>{columnTypes[colIndex]}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {Array.from({ length: cols }).map((_, colIndex) => {
              const cell = rowValues[colIndex] || ''
              if (columnTypes[colIndex] === 'file' && cell) {
                if (isImageFieldValue(cell, { tableColumnType: 'file' })) {
                  return (
                    <td key={`row-edit-cell-${colIndex}`}>
                      <ImageViewButton url={cell} muted={muted} />
                    </td>
                  )
                }
                return (
                  <td key={`row-edit-cell-${colIndex}`}>
                    <FileViewButton url={cell} muted={muted} />
                  </td>
                )
              }
              return (
                <td key={`row-edit-cell-${colIndex}`} className={muted ? 'text-muted' : undefined}>
                  {cell?.trim() ? cell : '—'}
                </td>
              )
            })}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const TableRowEditValuePreview: React.FC<{
  value: string
  tableFieldId: number
  fieldMetaById: Map<number, FormFieldDisplayMeta>
  rowIndex?: number
  muted?: boolean
}> = ({ value, tableFieldId, fieldMetaById, rowIndex = 0, muted = false }) => {
  const tableMeta = fieldMetaById.get(tableFieldId)
  if (!tableMeta || tableMeta.type !== 'table') {
    return (
      <span className={muted ? 'text-muted' : undefined} style={{ wordBreak: 'break-word' }}>
        {value || '—'}
      </span>
    )
  }

  const rowValues = parseTableRowFromValue(value, rowIndex, tableMeta.cols || 1)

  return <TableRowValuesTable rowValues={rowValues} tableMeta={tableMeta} muted={muted} />
}

export const getTableRowCompareContext = (
  edit: {
    fieldId: number
    originalValue: string
    submittedValue: string
    editType?: string
    scope?: string
    tableFieldId?: number
    rowIndex?: number
  },
  fieldMetaById: Map<number, FormFieldDisplayMeta>
): { tableFieldId: number; rowIndex: number } | null => {
  if (edit.editType === 'conditional') return null

  const tableFieldId =
    edit.tableFieldId ??
    (fieldMetaById.get(edit.fieldId)?.type === 'table' ? edit.fieldId : undefined)
  if (!tableFieldId) return null

  if (edit.scope === 'table_row') {
    return { tableFieldId, rowIndex: edit.rowIndex ?? 0 }
  }

  const tableMeta = fieldMetaById.get(tableFieldId)
  if (tableMeta?.type !== 'table') return null

  const original = String(edit.originalValue ?? '').trim()
  const submitted = String(edit.submittedValue ?? '').trim()
  if (!original.startsWith('[') && !submitted.startsWith('[')) return null

  return { tableFieldId, rowIndex: edit.rowIndex ?? 0 }
}

export const TableRowComparePreview: React.FC<{
  originalValue: string
  submittedValue: string
  tableFieldId: number
  rowIndex?: number
  fieldMetaById: Map<number, FormFieldDisplayMeta>
}> = ({ originalValue, submittedValue, tableFieldId, rowIndex = 0, fieldMetaById }) => {
  const tableMeta = fieldMetaById.get(tableFieldId)
  if (!tableMeta || tableMeta.type !== 'table') {
    return null
  }

  const cols = tableMeta.cols || 1
  const originalRow = parseTableRowFromValue(originalValue, rowIndex, cols)
  const submittedRow = parseTableRowFromValue(submittedValue, rowIndex, cols)

  return (
    <div className='submission-table-row-compare'>
      <div className='submission-table-row-compare__block'>
        <span className='submission-table-row-compare__label'>Previous</span>
        <TableRowValuesTable rowValues={originalRow} tableMeta={tableMeta} muted />
      </div>
      <div className='submission-table-row-compare__block'>
        <span className='submission-table-row-compare__label'>Changes</span>
        <TableRowValuesTable rowValues={submittedRow} tableMeta={tableMeta} />
      </div>
    </div>
  )
}

const TableValuePreview: React.FC<{ value: string; fieldMeta: FormFieldDisplayMeta }> = ({
  value,
  fieldMeta
}) => {
  const rows = fieldMeta.rows || 3
  const cols = fieldMeta.cols || 3
  const cells = parseTableCells(value, rows, cols)
  const columnHeaders = fieldMeta.columnHeaders || defaultColumnHeaders(cols)
  const rowHeaders = fieldMeta.rowHeaders || defaultRowHeaders(rows)
  const columnTypes = getColumnTypes({
    columnTypes: fieldMeta.columnTypes,
    tableColumnCount: cols
  })
  const visibleRows = cells
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => row.some((cell) => String(cell ?? '').trim()))

  if (visibleRows.length === 0) {
    const raw = String(value ?? '').trim()
    // Never swallow a value we failed to read as a grid — show it as text instead.
    if (raw && !parseTableMatrix(raw)) {
      return <span style={{ wordBreak: 'break-word' }}>{raw}</span>
    }
    return <span className='text-muted'>—</span>
  }

  return (
    <div className='submission-edit-table-preview'>
      <table className='table table-bordered mb-0 bg-white'>
        <thead>
          <tr>
            {fieldMeta.showRowHeaders ? <th style={{ width: 90 }} /> : null}
            {columnHeaders.slice(0, cols).map((header, colIndex) => (
              <th key={`col-${colIndex}`} className='text-muted fw-semibold'>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ row, rowIndex }) => (
            <tr key={`row-${rowIndex}`}>
              {fieldMeta.showRowHeaders ? (
                <th scope='row' className='text-muted fw-medium'>
                  {rowHeaders[rowIndex] || `Row ${rowIndex + 1}`}
                </th>
              ) : null}
              {row.slice(0, cols).map((cell, colIndex) => (
                <td key={`cell-${rowIndex}-${colIndex}`}>
                  {columnTypes[colIndex] === 'file' && cell?.trim() ? (
                    isImageFieldValue(cell, { tableColumnType: 'file' }) ? (
                      <ImageViewButton url={cell} />
                    ) : (
                      <FileViewButton url={cell} />
                    )
                  ) : cell?.trim() ? (
                    cell
                  ) : (
                    '—'
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const FileValuePreview: React.FC<{
  value: string
  fieldMeta?: FormFieldDisplayMeta
  editFieldType?: string
  muted?: boolean
}> = ({ value, fieldMeta, editFieldType, muted = false }) => {
  const trimmed = value.trim()
  if (!trimmed || !isHttpOrRelativeUrl(trimmed)) {
    return <span className='text-muted'>—</span>
  }

  if (isImageFieldValue(trimmed, { fieldMeta, editFieldType })) {
    return <ImageViewButton url={trimmed} muted={muted} />
  }

  if (
    fieldMeta?.type === 'file' ||
    editFieldType === 'file' ||
    isLikelyFileUrl(trimmed, fieldMeta)
  ) {
    return <FileViewButton url={trimmed} muted={muted} />
  }

  return null
}

interface SubmissionEditValueDisplayProps {
  value?: string | null
  fieldId: number
  fieldMetaById: Map<number, FormFieldDisplayMeta>
  muted?: boolean
  tableFieldId?: number
  tableColIndex?: number
  editFieldType?: string
  isTableRowEdit?: boolean
}

export const SubmissionEditValueDisplay: React.FC<SubmissionEditValueDisplayProps> = ({
  value,
  fieldId,
  fieldMetaById,
  muted = false,
  tableFieldId,
  tableColIndex,
  editFieldType,
  isTableRowEdit = false
}) => {
  const raw = String(value ?? '').trim()
  // Em dash / hyphen placeholders are empty — never treat them as file URLs.
  if (!raw || raw === '—' || raw === '-') return <span className='text-muted'>—</span>

  const fieldMeta = fieldMetaById.get(fieldId)
  const tableMeta = tableFieldId ? fieldMetaById.get(tableFieldId) : undefined
  const tableColumnType =
    tableMeta?.columnTypes && tableColIndex !== undefined
      ? tableMeta.columnTypes[tableColIndex]
      : undefined

  if (isTableRowEdit && tableFieldId) {
    return (
      <TableRowEditValuePreview
        value={raw}
        tableFieldId={tableFieldId}
        fieldMetaById={fieldMetaById}
        muted={muted}
      />
    )
  }

  if (
    isImageFieldValue(raw, {
      fieldMeta,
      editFieldType,
      tableColumnType
    })
  ) {
    return <ImageViewButton url={raw} muted={muted} />
  }

  const className = muted ? 'text-muted' : undefined

  if (fieldMeta?.type === 'checkbox' || editFieldType === 'checkbox') {
    return <ChoiceValuePreview value={raw} muted={muted} />
  }

  if (isLikelyTableValue(raw, fieldMeta)) {
    const tableMetaForPreview =
      fieldMeta?.type === 'table' ? fieldMeta : inferTableMetaFromValue(raw)
    return <TableValuePreview value={raw} fieldMeta={tableMetaForPreview} />
  }

  if (
    fieldMeta?.type === 'file' ||
    editFieldType === 'file' ||
    tableColumnType === 'file' ||
    isLikelyImageUrl(raw) ||
    /^https?:\/\//i.test(raw)
  ) {
    const filePreview = (
      <FileValuePreview
        value={raw}
        fieldMeta={fieldMeta}
        editFieldType={editFieldType || tableColumnType}
        muted={muted}
      />
    )
    if (
      fieldMeta?.type === 'file' ||
      editFieldType === 'file' ||
      tableColumnType === 'file' ||
      isLikelyImageUrl(raw) ||
      isLikelyFileUrl(raw, fieldMeta)
    ) {
      return filePreview
    }
  }

  return (
    <span className={className} style={{ wordBreak: 'break-word' }}>
      {raw}
    </span>
  )
}
